/**
 * Contact (Broker) API
 *
 * Read-only Dataverse API for broker contacts.
 * Queries the standard 'contacts' table, filtered to customertypecode = Broker.
 */

import {
  dataverseClient,
  type QueryOptions,
} from '@shared/api';
import type { DataverseEntity } from '@shared/api/dataverse/types';
import { escapeODataString } from '@shared/api/dataverse/utils';
import { assertGuid, assertDateLiteral } from '@shared/lib';
import type { BrokerContact, BrokerQueryParams } from '../model/types';

// =============================================================================
// Dataverse Entity Shape
// =============================================================================

interface ContactEntity extends DataverseEntity {
  contactid: string;
  fullname?: string;
  firstname?: string;
  lastname?: string;
  emailaddress1?: string;
  telephone1?: string;
  jobtitle?: string;
  address1_city?: string;
  address1_stateorprovince?: string;
  address1_latitude?: number;
  address1_longitude?: number;
  customertypecode?: number;
  'customertypecode@OData.Community.Display.V1.FormattedValue'?: string;
  _parentcustomerid_value?: string;
  '_parentcustomerid_value@OData.Community.Display.V1.FormattedValue'?: string;
  _dts_accountonly_value?: string;
  '_dts_accountonly_value@OData.Community.Display.V1.FormattedValue'?: string;
  _ownerid_value?: string;
  '_ownerid_value@OData.Community.Display.V1.FormattedValue'?: string;
  // Expanded parent account
  parentcustomerid_account?: {
    address1_city?: string;
    address1_stateorprovince?: string;
    address1_latitude?: number;
    address1_longitude?: number;
  };
  // Calculated fields
  crfc2_openopportunitiesbroker?: number;
  crfc2_wonopportunitiesbroker?: number;
  dts_lostopportunitiesbroker?: number;
  /**
   * Date of the broker's first won Veracity sale. Set when a broker first
   * crosses from Prospect → Existing. Null on brokers who have never had a
   * Veracity sale (i.e. still Prospect).
   */
  dts_dateoffirstveracitysaleforcontact?: string | null;
  birthdate?: string;
  statecode?: number;
  createdon?: string;
  modifiedon?: string;
}

// =============================================================================
// Broker Type Code Resolution
// =============================================================================

let brokerTypeCode: number | null = null;
let metadataPromise: Promise<void> | null = null;

interface MetadataResponse {
  OptionSet: {
    Options: Array<{
      Value: number;
      Label: {
        LocalizedLabels: Array<{ Label: string; LanguageCode: number }>;
      };
    }>;
  };
}

async function ensureBrokerTypeCode(): Promise<number> {
  if (brokerTypeCode !== null) return brokerTypeCode;
  if (metadataPromise) {
    await metadataPromise;
    if (brokerTypeCode === null) {
      throw new Error('[ContactAPI] Broker type code unresolved');
    }
    return brokerTypeCode;
  }

  metadataPromise = (async () => {
    try {
      const metadataPath =
        "EntityDefinitions(LogicalName='contact')/Attributes(LogicalName='customertypecode')" +
        '/Microsoft.Dynamics.CRM.PicklistAttributeMetadata' +
        '?$select=LogicalName&$expand=OptionSet($select=Options)';

      const response = await dataverseClient.fetchRaw<MetadataResponse>(metadataPath);
      const options = response.OptionSet?.Options ?? [];
      const brokerOption = options.find(
        (opt) => opt.Label?.LocalizedLabels?.[0]?.Label === 'Broker',
      );
      if (brokerOption?.Value == null) {
        throw new Error("[ContactAPI] 'Broker' label not found in customertypecode metadata");
      }
      brokerTypeCode = brokerOption.Value;
      console.log('[ContactAPI] Broker type code:', brokerTypeCode);
    } finally {
      // Clear the in-flight promise so a failed lookup can be retried on the next call.
      // Successful lookups keep brokerTypeCode populated, so subsequent calls short-circuit.
      if (brokerTypeCode === null) metadataPromise = null;
    }
  })();

  await metadataPromise;
  if (brokerTypeCode === null) {
    throw new Error('[ContactAPI] Broker type code unresolved');
  }
  return brokerTypeCode;
}

// =============================================================================
// Entity Mapping
// =============================================================================

function mapToDomain(entity: ContactEntity): BrokerContact {
  return {
    id: entity.contactid,
    fullName: entity.fullname ?? `${entity.firstname ?? ''} ${entity.lastname ?? ''}`.trim(),
    firstName: entity.firstname ?? undefined,
    lastName: entity.lastname ?? undefined,
    email: entity.emailaddress1 ?? undefined,
    phone: entity.telephone1 ?? undefined,
    jobTitle: entity.jobtitle ?? undefined,
    city: entity.address1_city ?? entity.parentcustomerid_account?.address1_city ?? undefined,
    state: entity.address1_stateorprovince ?? entity.parentcustomerid_account?.address1_stateorprovince ?? undefined,
    latitude: entity.address1_latitude ?? entity.parentcustomerid_account?.address1_latitude ?? undefined,
    longitude: entity.address1_longitude ?? entity.parentcustomerid_account?.address1_longitude ?? undefined,
    companyId: entity._dts_accountonly_value ?? entity._parentcustomerid_value ?? undefined,
    companyName:
      (entity['_dts_accountonly_value@OData.Community.Display.V1.FormattedValue'] as string)
      ?? (entity['_parentcustomerid_value@OData.Community.Display.V1.FormattedValue'] as string)
      ?? undefined,
    customerTypeCode: entity.customertypecode ?? undefined,
    customerTypeName:
      (entity['customertypecode@OData.Community.Display.V1.FormattedValue'] as string) ?? undefined,
    ownerId: entity._ownerid_value ?? undefined,
    ownerName:
      (entity['_ownerid_value@OData.Community.Display.V1.FormattedValue'] as string) ?? undefined,
    openOpportunities: entity.crfc2_openopportunitiesbroker ?? undefined,
    wonOpportunities: entity.crfc2_wonopportunitiesbroker ?? undefined,
    lostOpportunities: entity.dts_lostopportunitiesbroker ?? undefined,
    firstVeracitySaleDate: entity.dts_dateoffirstveracitysaleforcontact ?? undefined,
    birthDate: entity.birthdate ?? undefined,
    createdOn: entity.createdon ?? undefined,
    modifiedOn: entity.modifiedon ?? undefined,
    stateCode: entity.statecode ?? undefined,
  };
}

// =============================================================================
// Query Building
// =============================================================================

const SELECT_FIELDS = [
  'contactid', 'fullname', 'firstname', 'lastname',
  'emailaddress1', 'telephone1', 'jobtitle',
  'address1_city', 'address1_stateorprovince', 'address1_latitude', 'address1_longitude',
  'customertypecode',
  '_parentcustomerid_value',
  '_dts_accountonly_value',
  '_ownerid_value',
  'crfc2_openopportunitiesbroker', 'crfc2_wonopportunitiesbroker', 'dts_lostopportunitiesbroker',
  'dts_dateoffirstveracitysaleforcontact',
  'birthdate', 'createdon', 'modifiedon', 'statecode',
];

function buildQueryOptions(params: BrokerQueryParams, resolvedBrokerCode: number | null): QueryOptions {
  const options: QueryOptions = {
    select: SELECT_FIELDS,
    expand: ['parentcustomerid_account($select=address1_city,address1_stateorprovince,address1_latitude,address1_longitude)'],
    orderBy: 'fullname asc',
    top: params.top ?? 5000,
  };

  const filters: string[] = [];

  // Filter to broker contacts
  if (resolvedBrokerCode !== null) {
    filters.push(`customertypecode eq ${resolvedBrokerCode}`);
  }

  if (params.nameFilter) {
    filters.push(`contains(fullname, '${escapeODataString(params.nameFilter)}')`);
  }

  if (params.ownerId) {
    filters.push(`_ownerid_value eq '${assertGuid(params.ownerId, 'ownerId')}'`);
  }

  if (params.activeOnly !== false) {
    filters.push('statecode eq 0');
  }

  if (params.createdAfter) {
    filters.push(`createdon ge ${assertDateLiteral(params.createdAfter, 'createdAfter')}`);
  }

  if (params.createdBefore) {
    filters.push(`createdon le ${assertDateLiteral(params.createdBefore, 'createdBefore')}`);
  }

  if (filters.length > 0) {
    options.filter = filters.join(' and ');
  }

  return options;
}

// =============================================================================
// API Functions
// =============================================================================

async function listBrokers(params: BrokerQueryParams = {}): Promise<BrokerContact[]> {
  const code = await ensureBrokerTypeCode();
  const options = buildQueryOptions(params, code);
  const response = await dataverseClient.list<ContactEntity>('contacts', options);
  return response.value.map(mapToDomain);
}

async function getBroker(id: string): Promise<BrokerContact> {
  const options: QueryOptions = { select: SELECT_FIELDS, expand: ['parentcustomerid_account($select=address1_city,address1_stateorprovince,address1_latitude,address1_longitude)'] };
  const entity = await dataverseClient.get<ContactEntity>('contacts', id, options);
  return mapToDomain(entity);
}

async function searchBrokers(query: string, top = 20): Promise<BrokerContact[]> {
  return listBrokers({ nameFilter: query, top });
}

async function listUnclassifiedByOwner(ownerIds: string[]): Promise<BrokerContact[]> {
  if (ownerIds.length === 0) return [];
  const ownerFilter = ownerIds.map((id) => `_ownerid_value eq '${assertGuid(id, 'ownerId')}'`).join(' or ');
  const options: QueryOptions = {
    select: ['contactid', 'fullname', 'firstname', 'lastname', 'emailaddress1', '_parentcustomerid_value', '_dts_accountonly_value', '_ownerid_value', 'createdon'],
    filter: `customertypecode eq null and statecode eq 0 and (${ownerFilter})`,
    orderBy: 'fullname asc',
    top: 5000,
  };
  const response = await dataverseClient.list<ContactEntity>('contacts', options);
  return response.value.map(mapToDomain);
}

// =============================================================================
// Export
// =============================================================================

export const contactApi = {
  listBrokers,
  get: getBroker,
  searchBrokers,
  ensureBrokerTypeCode,
  listUnclassifiedByOwner,
};
